import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "invoiceSelect", "loading", "cobrosResults", "cobrosList",
    "receiptNumber", "colppyCoberId", "paymentDate", "paymentType",
    "percepcionIibb", "orderSelect", "orderAmounts", "orderAmountsList",
    "invoiceAmounts", "invoiceAmountsList", "orderPairs", "invoicePairs",
    "submitButton"
  ]

  static values = {
    patientId: Number,
    searchCobrosUrl: String
  }

  connect() {
    // Initialize order amount fields for pre-selected orders (e.g. initial_order)
    this.updateOrderAmountFields()
    // Idem para la factura preseleccionada desde el aviso "En Colppy figura cobrada".
    this.updateInvoiceAmountFields()
  }

  async searchCobros() {
    if (!this.hasInvoiceSelectTarget) return
    const invoiceId = this.invoiceSelectTarget.value
    if (!invoiceId) return

    this.loadingTarget.style.display = "block"
    this.cobrosResultsTarget.style.display = "none"

    try {
      const url = `${this.searchCobrosUrlValue}?invoice_id=${invoiceId}`
      const response = await fetch(url, {
        headers: { "Accept": "application/json", "X-CSRF-Token": this.csrfToken }
      })
      const data = await response.json()

      this.loadingTarget.style.display = "none"

      if (data.success && data.cobros && data.cobros.length > 0) {
        this.displayCobros(data.cobros)
      } else {
        this.cobrosResultsTarget.style.display = "block"
        this.cobrosListTarget.innerHTML = `<div class="alert alert-info py-2"><small>${data.error || "No se encontraron cobros para esta factura"}</small></div>`
      }
    } catch (error) {
      this.loadingTarget.style.display = "none"
      this.cobrosResultsTarget.style.display = "block"
      this.cobrosListTarget.innerHTML = `<div class="alert alert-danger py-2"><small>Error: ${error.message}</small></div>`
    }
  }

  displayCobros(cobros) {
    this.cobrosResultsTarget.style.display = "block"
    this.cobrosListTarget.innerHTML = ""

    cobros.forEach((cobro) => {
      const totalPesos = (cobro.total_cobro_cents / 100).toFixed(2)
      const isFullyRegistered = cobro.already_registered
      const registeredAmount = cobro.registered_amount_cents || 0
      const remainingCents = cobro.remaining_cents || cobro.total_cobro_cents
      const isPartiallyRegistered = registeredAmount > 0 && !isFullyRegistered
      const registeredPesos = (registeredAmount / 100).toFixed(2)
      const remainingPesos = (remainingCents / 100).toFixed(2)

      const div = document.createElement("div")
      div.className = `p-2 mb-2 border rounded ${isFullyRegistered ? "bg-light text-muted" : "border-primary"}`

      let badgeHtml = ""
      if (isFullyRegistered) {
        badgeHtml = '<span class="badge badge-secondary ml-1">Ya registrado</span>'
      } else if (isPartiallyRegistered) {
        badgeHtml = `<span class="badge badge-warning ml-1">Registrado parcial: $${registeredPesos} — Disponible: $${remainingPesos}</span>`
      }

      div.innerHTML = `
        <div class="d-flex justify-content-between align-items-center">
          <div>
            <strong>Recibo #${cobro.nro_recibo_full || cobro.nro_recibo || cobro.id_cobro}</strong>
            <span class="ml-2">${cobro.fecha_cobro || ""}</span>
            <span class="ml-2">$${totalPesos}</span>
            ${badgeHtml}
          </div>
          ${isFullyRegistered ? "" : `
            <button type="button" class="btn btn-sm btn-outline-primary"
                    data-action="click->colppy-import-payment#selectCobro"
                    data-cobro-id="${cobro.id_cobro}"
                    data-cobro-receipt="${cobro.nro_recibo || ""}"
                    data-cobro-date="${cobro.fecha_cobro || ""}"
                    data-cobro-total="${totalPesos}">
              <i class="fas fa-check mr-1"></i> Seleccionar
            </button>
          `}
        </div>
      `
      this.cobrosListTarget.appendChild(div)
    })
  }

  selectCobro(event) {
    const btn = event.currentTarget
    const cobroId = btn.dataset.cobroId
    const receipt = btn.dataset.cobroReceipt
    const date = btn.dataset.cobroDate
    const total = btn.dataset.cobroTotal

    if (this.hasReceiptNumberTarget) this.receiptNumberTarget.value = receipt
    if (this.hasColppyCoberIdTarget) this.colppyCoberIdTarget.value = cobroId
    const cobroIdField = this.element.querySelector('[name="colppy_payment[colppy_cobro_id]"]')
    if (cobroIdField) cobroIdField.value = cobroId
    if (this.hasPaymentDateTarget && date) this.paymentDateTarget.value = date

    // Highlight selected
    this.cobrosListTarget.querySelectorAll(".border-primary").forEach(el => el.classList.remove("border-primary"))
    btn.closest(".border").classList.add("border-primary")

    this.updateSubmitState()
  }

  updateOrderAmountFields() {
    requestAnimationFrame(() => {
      const orderSelect = this.orderSelectTarget
      const selectedValues = Array.from(orderSelect.selectedOptions || [])

      if (selectedValues.length > 0) {
        this.orderAmountsTarget.style.display = "block"
        this.orderAmountsListTarget.innerHTML = ""

        selectedValues.forEach((option) => {
          const orderId = option.value
          if (!orderId) return

          const orderText = option.text
          const remainingCents = parseInt(option.getAttribute("data-order-remaining")) || 0
          const remainingPesos = remainingCents / 100

          const amountDiv = document.createElement("div")
          amountDiv.className = "form-group"
          amountDiv.innerHTML = `
            <label>${orderText}</label>
            <input type="number"
                   name="order_amount_${orderId}"
                   class="form-control colppy-import-payment-order-amount"
                   min="0"
                   max="${remainingPesos.toFixed(2)}"
                   step="0.01"
                   value="${remainingPesos.toFixed(2)}"
                   data-order-id="${orderId}"
                   data-remaining="${remainingCents}"
                   data-action="input->colppy-import-payment#updateOrderPairs blur->colppy-import-payment#formatInputValue">
          `
          this.orderAmountsListTarget.appendChild(amountDiv)
        })

        this.updateOrderPairs()
      } else {
        this.orderAmountsTarget.style.display = "none"
        this.orderAmountsListTarget.innerHTML = ""
        this.orderPairsTarget.value = "{}"
      }

      this.updateSubmitState()
    })
  }

  updateInvoiceAmountFields() {
    if (!this.hasInvoiceAmountsTarget) return

    requestAnimationFrame(() => {
      const invoiceSelect = this.element.querySelector('[name="invoice_ids[]"]')
      if (!invoiceSelect) return

      const selectedValues = Array.from(invoiceSelect.selectedOptions || [])

      if (selectedValues.length > 0) {
        this.invoiceAmountsTarget.style.display = "block"
        this.invoiceAmountsListTarget.innerHTML = ""

        selectedValues.forEach((option) => {
          const invoiceId = option.value
          if (!invoiceId) return

          const invoiceText = option.text
          const remainingCents = parseInt(option.getAttribute("data-invoice-remaining")) || 0
          const remainingPesos = remainingCents / 100

          const amountDiv = document.createElement("div")
          amountDiv.className = "form-group"
          amountDiv.innerHTML = `
            <label>${invoiceText}</label>
            <input type="number"
                   name="invoice_amount_${invoiceId}"
                   class="form-control colppy-import-payment-invoice-amount"
                   min="0"
                   max="${remainingPesos.toFixed(2)}"
                   step="0.01"
                   value="${remainingPesos.toFixed(2)}"
                   data-invoice-id="${invoiceId}"
                   data-action="input->colppy-import-payment#updateInvoicePairs blur->colppy-import-payment#formatInputValue">
          `
          this.invoiceAmountsListTarget.appendChild(amountDiv)
        })

        this.updateInvoicePairs()
      } else {
        this.invoiceAmountsTarget.style.display = "none"
        this.invoiceAmountsListTarget.innerHTML = ""
        this.invoicePairsTarget.value = ""
      }

      this.updateSubmitState()
    })
  }

  formatInputValue(event) {
    const input = event.target
    const value = parseFloat(input.value) || 0
    input.value = value.toFixed(2)
  }

  updateOrderPairs() {
    const pairs = {}
    this.element.querySelectorAll(".colppy-import-payment-order-amount").forEach((input) => {
      const orderId = input.dataset.orderId
      const amountPesos = parseFloat(input.value) || 0
      const amountCents = Math.round(amountPesos * 100)
      if (orderId && amountCents > 0) {
        pairs[orderId] = amountCents
      }
    })
    this.orderPairsTarget.value = JSON.stringify(pairs)
    this.updateSubmitState()
  }

  updateInvoicePairs() {
    const pairs = {}
    this.element.querySelectorAll(".colppy-import-payment-invoice-amount").forEach((input) => {
      const invoiceId = input.dataset.invoiceId
      const amountPesos = parseFloat(input.value) || 0
      const amountCents = Math.round(amountPesos * 100)
      if (invoiceId && amountCents > 0) {
        pairs[invoiceId] = amountCents
      }
    })
    this.invoicePairsTarget.value = JSON.stringify(pairs)
    this.updateSubmitState()
  }

  updateSubmitState() {
    if (!this.hasSubmitButtonTarget) return

    // Alcanza con una orden O una factura con monto positivo. Un cobro puede
    // saldar una factura sin tocar ninguna orden: pasa con las facturas
    // importadas de Colppy, que cubren mas que la orden que tienen colgada.
    const hasPositiveAmount = (selector) =>
      Array.from(this.element.querySelectorAll(selector))
        .some((input) => (parseFloat(input.value) || 0) > 0)

    this.submitButtonTarget.disabled =
      !hasPositiveAmount(".colppy-import-payment-order-amount") &&
      !hasPositiveAmount(".colppy-import-payment-invoice-amount")
  }

  get csrfToken() {
    const meta = document.querySelector('meta[name="csrf-token"]')
    return meta ? meta.getAttribute("content") : ""
  }
}
